// Business Outcomes — tenant-specific KPIs (Prisma Ortho: scheduled /
// rescheduled / confirmed / cancelled / MyChart message counts).
//
// Four panels:
//   1. Outcome Totals — per-outcome cards with sparkline + true
//                       period-over-period Δ (prior window = same length,
//                       immediately preceding). Click the big number for
//                       the current-window sessions; click the Δ badge
//                       for the prior-window sessions behind the compare.
//                       Deep-verify toggle fetches messages to get the
//                       GROUND TRUTH count (session-row lastAction misses
//                       outcomes whose tool fired mid-session but wasn't
//                       the final call). Δ is computed off the DISPLAYED
//                       count (verified when deep-mode is on, otherwise
//                       fast) so a deep verify also refreshes the Δ.
//                       Prior-window sessions are fetched via a lightweight
//                       inline AI_SESSIONS_QUERY (no tasks/legs fan-out) so
//                       the cost is 1-3 slices rather than the full 3-root
//                       bisection useReportWindow runs.
//   2. Per-Bot Fallout — table: bot | total | HANDLED% | ESCALATED% |
//                        ABANDONED% | ERRORED% | fallout rate.
//   3. Also-ran HANDLED — sessions that completed HANDLED but did NOT
//                         produce a tracked business outcome. Surfaces
//                         sessions ending on GetSlots / GetScheduleForProvider
//                         (caller browsed but didn't book) — worth
//                         understanding separately.
//   4. Non-prod leakage — bots without the PROD- prefix whose stem matches
//                         a prod bot. Flags test traffic bleeding into
//                         business KPIs.
//
// Data source: shared `data` prop (contactAIAgentSession rows for the
// current window). Deep mode uses `callTool('ops', 'wxcc-operations-list-
// ai-agents')` once + `wxcc-operations-get-ai-agent-session-messages` per
// candidate session (rate-limited 2.2s/req by Cisco).
//
// Config source: static botCatalog.json → bots[].business_outcomes.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle, IconPlayerStopFilled, IconSparkles } from '@tabler/icons-react';

import catalogData from '../data/botCatalog.json';
import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';
import { classifyFast, classifyDeep, listOutcomes, trendByDay } from '../lib/businessOutcomes';
import { callTool } from '../../../api_scripts/wxccMcpClient';
import { fetchAllPaged } from '../../../api_scripts/wxccPagingClient';

const REPORT_ID = 'business-outcomes';

// Inlined copy of useReportWindow's AI sessions query — the prior-window
// fetch only needs sessions (classifyFast reads only s.agentName, s.agentId,
// s.lastAction, s.interactionOutcome, s.sessionStartTime, s.sessionId), so
// we skip the full tasks/legs fan-out. Kept in sync with
// `useReportWindow.js` — if that query grows a field classifyFast relies
// on, mirror the change here. (The decision to inline rather than export
// from useReportWindow was deliberate: that hook is a public contract for
// multiple reports; exporting a bare GraphQL string couples them.)
const PRIOR_WINDOW_SESSIONS_QUERY = `
  query AiSessions($from: Long!, $to: Long!) {
    contactAIAgentSession(from: $from, to: $to) {
      data {
        interactionId sessionId agentId agentName agentType
        interactionOutcome channelType ani
        sessionStartTime sessionEndTime duration
        noOfTotalMessages lastAction
      }
      pageInfo { endCursor hasNextPage }
    }
  }
`;

// ±5% is "neutral" — nothing to see, don't light up the dashboard with
// weekend-vs-weekday noise. Over 5% in either direction, we show the
// Δ badge coloured green (up) or red (down). Caller can click the badge
// to inspect the prior-window sessions behind the comparison.
const DELTA_NEUTRAL_PCT = 5;

export function BusinessOutcomes({ data, onViewSession, window }) {
  // Stabilise `sessions` identity so useMemos below don't recompute on
  // every render (CRA's react-hooks/exhaustive-deps catches `data?.sessions ?? []`
  // creating a new array each render).
  const sessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);
  const [agentIdByName, setAgentIdByName] = useState({});
  // Reverse map agentId -> canonical bot name. Business Outcomes classifier
  // uses this to resolve sessions whose `agentName` is empty but whose
  // `agentId` is populated — observed 2026-10-26: ~32% of OrthoMain sessions
  // and ~45% of TESTPROD-Ortho_VisitApptType_DT sessions landed with empty
  // agentName. Without this fallback the dashboard was systematically
  // undercounting Prisma Ortho volumes.
  const [agentIdToName, setAgentIdToName] = useState({});
  const [deepResult, setDeepResult] = useState(null);
  const [deepRunning, setDeepRunning] = useState(false);
  const [deepProgress, setDeepProgress] = useState({ done: 0, total: 0 });
  const [deepError, setDeepError] = useState(null);
  const abortRef = useRef(null);
  const [drill, setDrill] = useState(null); // { title, sessions }

  // Prior-window compare state. Lazy-fetched on window change — if the
  // user never looks at the Business Outcomes panel we don't want to burn
  // another ~2s/slice on prior-window pagination.
  const [priorSessions, setPriorSessions] = useState(null);
  const [priorLoading, setPriorLoading] = useState(false);
  const [priorError, setPriorError] = useState(null);
  const priorAbortRef = useRef(null);

  // One-time: fetch agent id map so Deep mode can call
  // wxcc-operations-get-ai-agent-session-messages (requires agentId).
  useEffect(() => {
    let cancelled = false;
    callTool('ops', 'wxcc-operations-list-ai-agents', { view: 'list' })
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const reverseMap = {};
        for (const a of res?.agents ?? []) {
          if (a.name && a.id) {
            map[a.name] = a.id;
            reverseMap[a.id] = a.name;
          }
        }
        setAgentIdByName(map);
        setAgentIdToName(reverseMap);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Prior window: same length as the current window, immediately preceding.
  // [from - (to - from), from]. Null if no window prop was passed in (the
  // report CAN render without one; prior Δ just stays hidden).
  const priorWindow = useMemo(() => {
    if (!window?.from || !window?.to) return null;
    const span = window.to - window.from;
    if (!Number.isFinite(span) || span <= 0) return null;
    return { from: window.from - span, to: window.from };
  }, [window?.from, window?.to]);

  // Serialised prior-window pagination — only `data` (session rows),
  // no tasks/legs. Reset state whenever the window moves. Shares the
  // 2.2s/req pacing with Deep verify by virtue of fetchAllPaged itself.
  useEffect(() => {
    priorAbortRef.current?.abort();
    if (!priorWindow) {
      setPriorSessions(null);
      setPriorError(null);
      setPriorLoading(false);
      return;
    }
    const controller = new AbortController();
    priorAbortRef.current = controller;
    setPriorSessions(null);
    setPriorError(null);
    setPriorLoading(true);
    (async () => {
      try {
        const { rows } = await fetchAllPaged(
          PRIOR_WINDOW_SESSIONS_QUERY,
          'contactAIAgentSession',
          'data',
          'sessionId',
          { from: priorWindow.from, to: priorWindow.to, signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        setPriorSessions(rows);
        setPriorLoading(false);
      } catch (e) {
        if (controller.signal.aborted) return;
        setPriorError(e?.message || String(e));
        setPriorLoading(false);
      }
    })();
    return () => controller.abort();
  }, [priorWindow]);

  const fast = useMemo(() => classifyFast(sessions, catalogData, { agentIdToName }), [sessions, agentIdToName]);
  const outcomeDefs = useMemo(() => listOutcomes(catalogData), []);

  // Fast classifier against prior-window sessions. Only runs when we have
  // both the rows and the agent-id map — otherwise bots with empty
  // agentName land in `(unknown)` and skew the prior count low.
  const priorFast = useMemo(() => {
    if (!priorSessions) return null;
    return classifyFast(priorSessions, catalogData, { agentIdToName });
  }, [priorSessions, agentIdToName]);

  // Daily sparkline per outcome — buckets `sessions` by day in the window's
  // local TZ and emits [{ day, count }] for each outcome's tool-name set.
  // (True period-over-period Δ against the PRIOR window is handled further
  // down via a separate fetchAllPaged call — see PriorDelta.)
  const trendByOutcome = useMemo(() => {
    const out = {};
    for (const d of outcomeDefs) out[d.outcome] = trendByDay(sessions, catalogData, d.outcome, undefined, { agentIdToName });
    return out;
  }, [sessions, outcomeDefs, agentIdToName]);

  async function runDeepVerify() {
    if (deepRunning) return;
    setDeepRunning(true);
    setDeepError(null);
    setDeepProgress({ done: 0, total: 0 });
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await classifyDeep(sessions, catalogData, {
        agentIdByName,
        agentIdToName,
        signal: controller.signal,
        onProgress: (done, total) => setDeepProgress({ done, total }),
        fetchMessages: async (agentId, sessionId) => {
          // Rate-limit: 2.2s between calls. The MCP server itself throttles
          // at ~1 req/2s so we sleep BEFORE every call to stay under the cap.
          await new Promise((r) => setTimeout(r, 2200));
          if (controller.signal.aborted) throw new Error('aborted');
          return callTool('ops', 'wxcc-operations-get-ai-agent-session-messages', {
            agentId, sessionId, limit: 100,
          });
        },
      });
      setDeepResult(result);
    } catch (e) {
      setDeepError(e?.message || String(e));
    } finally {
      setDeepRunning(false);
      abortRef.current = null;
    }
  }

  function cancelDeep() {
    abortRef.current?.abort();
    setDeepRunning(false);
  }

  const result = deepResult ?? fast;
  const isDeep = result?.mode === 'deep';

  if (outcomeDefs.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
        No <code>business_outcomes</code> declared in <code>botCatalog.json</code> for this tenant. Add
        a <code>business_outcomes</code> block to any bot you want tracked here.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={4}>Business Outcomes</Title>
          <Text size="xs" c="dimmed">
            Per-outcome session counts for the current window. Fast mode uses{' '}
            <code>lastAction</code> only (misses outcomes whose tool fired mid-session). Deep
            mode fetches per-session messages for the ground truth count (slow — ~2.2s per
            candidate session due to the Cisco API rate limit).
          </Text>
        </div>
        <Group gap="xs">
          {!deepRunning && !isDeep && (
            <Tooltip label="Fetch per-session messages for every candidate to get the ground-truth count. Rate-limited.">
              <Button
                size="xs"
                variant="light"
                color="violet"
                leftSection={<IconSparkles size={14} />}
                onClick={runDeepVerify}
                disabled={sessions.length === 0 || Object.keys(agentIdByName).length === 0}
              >
                Deep verify
              </Button>
            </Tooltip>
          )}
          {deepRunning && (
            <>
              <Text size="xs" c="dimmed">
                Verifying {deepProgress.done}/{deepProgress.total}
              </Text>
              <Button size="xs" variant="light" color="red" leftSection={<IconPlayerStopFilled size={14} />} onClick={cancelDeep}>
                Cancel
              </Button>
            </>
          )}
          {isDeep && !deepRunning && (
            <Badge color="violet" variant="light">Deep-verified ({deepProgress.done} sessions)</Badge>
          )}
        </Group>
      </Group>

      {deepError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={14} />}>
          Deep verify failed: {deepError}
        </Alert>
      )}

      {/* Panel 1 — Outcome Totals */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: Math.min(outcomeDefs.length, 5) }} spacing="sm">
        {outcomeDefs.map((d) => {
          const fastCount = result.outcomes?.[d.outcome] ?? 0;
          const verifiedCount = result.outcomesVerified?.[d.outcome];
          const trend = trendByOutcome[d.outcome] ?? [];
          const priorCount = priorFast?.outcomes?.[d.outcome];
          return (
            <OutcomeCard
              key={d.outcome}
              label={d.outcome}
              botName={d.botName}
              toolNames={[...d.toolNames]}
              fastCount={fastCount}
              verifiedCount={verifiedCount}
              trend={trend}
              priorCount={priorCount}
              priorLoading={priorLoading}
              priorError={priorError}
              priorWindow={priorWindow}
              onDrill={() => {
                const matchIds = new Set(
                  isDeep
                    ? (result.verifiedSessionsByOutcome?.[d.outcome] ?? [])
                    : sessionsMatchingOutcome(sessions, d, agentIdToName)
                );
                setDrill({
                  title: `${d.outcome} — ${matchIds.size} session${matchIds.size === 1 ? '' : 's'}`,
                  sessions: sessions.filter((s) => matchIds.has(s.sessionId)),
                });
              }}
              onDrillPrior={() => {
                // Drill to the PRIOR-window sessions that would've matched
                // this outcome. Same lastAction+bot filter the current fast
                // count uses, applied to priorSessions.
                if (!priorSessions) return;
                const matchIds = new Set(sessionsMatchingOutcome(priorSessions, d, agentIdToName));
                setDrill({
                  title: `${d.outcome} — prior window (${matchIds.size} session${matchIds.size === 1 ? '' : 's'})`,
                  sessions: priorSessions.filter((s) => matchIds.has(s.sessionId)),
                });
              }}
            />
          );
        })}
      </SimpleGrid>

      {/* Panel 2 — Per-Bot Fallout */}
      <FalloutTable fallout={result.fallout} />

      {/* Panel 3 — Also-ran HANDLED */}
      <AlsoRanTable rows={result.alsoRanHandled} />

      {/* Panel 4 — Non-Prod Leakage */}
      <NonProdLeakageAlert rows={result.nonProdLeakage} />

      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title ?? ''}
        sessions={drill?.sessions ?? []}
        onViewSession={onViewSession}
        window={window}
      />
    </Stack>
  );
}

function sessionsMatchingOutcome(sessions, def, agentIdToName = {}) {
  const toolSet = def.toolNames;
  const resolveBotName = (s) => s.agentName || (s.agentId && agentIdToName[s.agentId]) || '';
  return sessions
    .filter((s) => resolveBotName(s) === def.botName && toolSet.has(s.lastAction))
    .map((s) => s.sessionId);
}

function OutcomeCard({
  label,
  botName,
  toolNames,
  fastCount,
  verifiedCount,
  trend,
  priorCount,
  priorLoading,
  priorError,
  priorWindow,
  onDrill,
  onDrillPrior,
}) {
  const display = verifiedCount ?? fastCount;
  const diff = verifiedCount != null ? verifiedCount - fastCount : null;
  return (
    <Paper withBorder p="md" radius="md" style={{ cursor: 'pointer' }} onClick={onDrill}>
      <Text size="xs" c="dimmed" fw={500}>{label}</Text>
      <Group gap={4} align="baseline" mt={2}>
        <Text fw={700} style={{ fontSize: 28, lineHeight: 1 }}>{display}</Text>
        {diff != null && (
          <Tooltip label={`Fast count was ${fastCount}; deep verify found ${verifiedCount}. Δ=${diff >= 0 ? '+' : ''}${diff}.`}>
            <Badge size="xs" color={diff === 0 ? 'gray' : (diff > 0 ? 'violet' : 'orange')} variant="light">
              {diff >= 0 ? '+' : ''}{diff}
            </Badge>
          </Tooltip>
        )}
      </Group>
      <PriorDelta
        current={display}
        prior={priorCount}
        loading={priorLoading}
        error={priorError}
        priorWindow={priorWindow}
        onDrillPrior={onDrillPrior}
      />
      <Text size="xs" c="dimmed" mt={4}>
        {botName.replace(/^PROD-/, '')} · <code>{toolNames.join(', ')}</code>
      </Text>
      <TrendSparkline points={trend.map((p) => p.count)} days={trend.map((p) => p.day)} />
    </Paper>
  );
}

// Period-over-period Δ. Prior window = same length as current, immediately
// preceding. ±5% neutral (grey). Shows the raw delta on hover + the prior
// count + the prior window range. Clicking the badge opens the drill-
// through modal with the prior-window sessions behind it.
function PriorDelta({ current, prior, loading, error, priorWindow, onDrillPrior }) {
  if (loading) {
    return (
      <Group gap={6} mt={6}>
        <Loader size="xs" />
        <Text size="xs" c="dimmed">comparing to prior window…</Text>
      </Group>
    );
  }
  if (error) {
    return (
      <Text size="xs" c="red" mt={6}>Δ unavailable — prior-window fetch failed.</Text>
    );
  }
  if (prior == null) return null;

  // Current == prior == 0 → both empty, nothing meaningful to say.
  if (current === 0 && prior === 0) {
    return <Text size="xs" c="dimmed" mt={6}>No prior-window activity to compare.</Text>;
  }

  // Division-by-zero guard: if prior was 0 and current isn't, the % is
  // infinite — surface as "new" rather than a bogus number.
  if (prior === 0) {
    return (
      <Group gap={6} mt={6} onClick={(e) => { e.stopPropagation(); onDrillPrior?.(); }}>
        <Tooltip label={priorWindowTooltip(prior, priorWindow)}>
          <Badge size="xs" color="teal" variant="light" style={{ cursor: 'pointer' }}>
            ▲ new vs. prior window
          </Badge>
        </Tooltip>
      </Group>
    );
  }

  const absPct = Math.round(((current - prior) / prior) * 100);
  const direction = absPct > 0 ? '▲' : absPct < 0 ? '▼' : '•';
  const color =
    Math.abs(absPct) <= DELTA_NEUTRAL_PCT
      ? 'gray'
      : absPct > 0
      ? 'teal'
      : 'red';
  const sign = absPct > 0 ? '+' : '';
  return (
    <Group gap={6} mt={6} onClick={(e) => { e.stopPropagation(); onDrillPrior?.(); }}>
      <Tooltip label={priorWindowTooltip(prior, priorWindow)}>
        <Badge size="xs" color={color} variant="light" style={{ cursor: 'pointer' }}>
          {direction} {sign}{absPct}% vs. prior window
        </Badge>
      </Tooltip>
    </Group>
  );
}

function priorWindowTooltip(priorCount, priorWindow) {
  const range = priorWindow
    ? `${new Date(priorWindow.from).toLocaleString()} → ${new Date(priorWindow.to).toLocaleString()}`
    : 'prior window';
  return `Prior-window count: ${priorCount} · ${range}. Click to drill into those sessions.`;
}

function TrendSparkline({ points, days }) {
  if (!points || points.length === 0) return <Text size="xs" c="dimmed" mt={6}>No data in window.</Text>;
  const max = Math.max(1, ...points);
  const w = 160;
  const h = 28;
  const stepX = points.length > 1 ? w / (points.length - 1) : w;
  const pathD = points.map((v, i) => {
    const x = i * stepX;
    const y = h - (v / max) * h;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const total = points.reduce((a, b) => a + b, 0);
  const latestDay = days?.[days.length - 1];
  const latestCount = points[points.length - 1];
  return (
    <Tooltip label={`${days?.length || 0} days · last day ${latestDay}: ${latestCount}`}>
      <div style={{ marginTop: 6 }}>
        <svg width={w} height={h} style={{ display: 'block' }}>
          <path d={pathD} fill="none" stroke="var(--mantine-color-blue-5)" strokeWidth={1.5} />
          {points.map((v, i) => (
            <circle key={i} cx={i * stepX} cy={h - (v / max) * h} r={2} fill="var(--mantine-color-blue-6)" />
          ))}
        </svg>
        <Text size="xs" c="dimmed">{total} over {points.length}d</Text>
      </div>
    </Tooltip>
  );
}

const FALLOUT_COLS = [
  { id: 'bot',       label: 'Bot',       pinned: true, render: (r) => <Text size="sm" fw={500}>{r.bot.replace(/^PROD-/, '')}</Text> },
  { id: 'total',     label: 'Sessions',  align: 'right', render: (r) => <Text size="sm" fw={600}>{r.total}</Text> },
  { id: 'handled',   label: 'HANDLED',   align: 'right', render: (r) => <Badge size="sm" color="teal" variant="light">{r.handled} · {pct(r.handled, r.total)}%</Badge> },
  { id: 'escalated', label: 'ESCALATED', align: 'right', render: (r) => <Badge size="sm" color="blue" variant="light">{r.escalated} · {pct(r.escalated, r.total)}%</Badge> },
  { id: 'abandoned', label: 'ABANDONED', align: 'right', render: (r) => <Badge size="sm" color="orange" variant="light">{r.abandoned} · {pct(r.abandoned, r.total)}%</Badge> },
  { id: 'errored',   label: 'ERRORED',   align: 'right', render: (r) => <Badge size="sm" color="red" variant="light">{r.errored} · {pct(r.errored, r.total)}%</Badge> },
  {
    id: 'fallout', label: 'Fallout rate', align: 'right',
    hint: '(Escalated + Abandoned + Errored) / total. Green <20%, yellow 20-50%, red >50%.',
    render: (r) => {
      const rate = r.total ? Math.round(((r.escalated + r.abandoned + r.errored) / r.total) * 100) : 0;
      const color = rate < 20 ? 'teal' : rate < 50 ? 'yellow' : 'red';
      return (
        <div style={{ minWidth: 110 }}>
          <Progress value={rate} color={color} size="sm" />
          <Text size="xs" c="dimmed" mt={2}>{rate}%</Text>
        </div>
      );
    },
  },
];

function FalloutTable({ fallout }) {
  const rows = Object.entries(fallout ?? {}).map(([bot, v]) => ({ key: bot, bot, ...v }));
  rows.sort((a, b) => b.total - a.total);
  if (rows.length === 0) {
    return (
      <Paper withBorder p="md" radius="md">
        <Title order={6} mb="xs">Per-Bot Fallout</Title>
        <Text size="sm" c="dimmed">No target-bot sessions in this window.</Text>
      </Paper>
    );
  }
  return (
    <ReportTable
      title="Per-Bot Fallout"
      subtitle="Outcome distribution for every bot that has a business_outcomes block in the catalog."
      reportId={`${REPORT_ID}-fallout`}
      columns={FALLOUT_COLS}
      defaultVisibleIds={['total', 'handled', 'escalated', 'abandoned', 'errored', 'fallout']}
      rows={rows}
      emptyLabel="No target-bot sessions."
      csvExport={{
        filename: `business-outcomes-fallout-${new Date().toISOString().slice(0, 10)}.csv`,
        rows: rows.map((r) => ({ bot: r.bot, total: r.total, handled: r.handled, escalated: r.escalated, abandoned: r.abandoned, errored: r.errored, fallout_pct: pct(r.escalated + r.abandoned + r.errored, r.total) })),
      }}
    />
  );
}

const ALSO_RAN_COLS = [
  { id: 'bot',        label: 'Bot',        pinned: true, render: (r) => <Text size="sm" fw={500}>{r.botName.replace(/^PROD-/, '')}</Text> },
  { id: 'lastAction', label: 'lastAction', pinned: true, render: (r) => <Text size="sm"><code>{r.lastAction || '(none)'}</code></Text> },
  { id: 'count',      label: 'HANDLED sessions', align: 'right', render: (r) => <Badge size="sm" color="yellow" variant="light">{r.count}</Badge> },
];

function AlsoRanTable({ rows }) {
  const r = rows ?? [];
  if (r.length === 0) return null;
  return (
    <ReportTable
      title="Also-ran HANDLED — completed cleanly but did NOT produce a tracked business outcome"
      subtitle="HANDLED sessions whose lastAction does NOT map to any business outcome. Common cause: caller browsed slots / asked a question / confirmed something, then exited without a booking. Worth tracking separately."
      reportId={`${REPORT_ID}-also-ran`}
      columns={ALSO_RAN_COLS}
      defaultVisibleIds={['count']}
      rows={r.map((row, i) => ({ ...row, key: `${row.botName}|${row.lastAction}|${i}` }))}
      emptyLabel="No also-ran HANDLED sessions."
      csvExport={{
        filename: `business-outcomes-alsoran-${new Date().toISOString().slice(0, 10)}.csv`,
        rows: r,
      }}
    />
  );
}

function NonProdLeakageAlert({ rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} title={`Non-prod leakage — ${rows.length} bot${rows.length === 1 ? '' : 's'} without PROD- prefix matched a prod bot name`}>
      <Stack gap={4}>
        {rows.map((r) => (
          <Text size="xs" key={r.botName}>
            <code>{r.botName}</code> — {r.count} session{r.count === 1 ? '' : 's'} (matches prod bot <code>{r.prodCounterpart}</code>). Not counted in Business Outcomes totals.
          </Text>
        ))}
      </Stack>
    </Alert>
  );
}

function pct(a, b) {
  if (!b) return 0;
  return Math.round((a / b) * 100);
}
